import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { View } from 'react-native';
import { HelperText } from 'react-native-paper';

import type { DropdownItem } from '@/shared/components/DropdownSelect';
import { DropdownSelect } from '@/shared/components/DropdownSelect';
import { isEmptyOrNull } from '@/shared/utils/string';

type Props<T extends FieldValues, V extends string> = {
  name: Path<T>;
  control: Control<T>;
  options: DropdownItem<V>[];
  label: string;
  placeholder: string;
  helperText?: string;
  editable?: boolean;
  maxVisibleItems?: number;
  matchInputWidth?: boolean;
  onSelect?: (value: V[]) => void;
};

export const FormSingleSelect = <T extends FieldValues, V extends string>({
  name,
  control,
  options,
  label,
  placeholder,
  helperText,
  editable,
  maxVisibleItems,
  matchInputWidth,
  onSelect
}: Props<T, V>) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { value, onChange }, fieldState: { error } }) => (
      <View>
        <DropdownSelect<V>
          options={options}
          label={label}
          placeholder={placeholder}
          editable={editable}
          maxVisibleItems={maxVisibleItems}
          matchInputWidth={matchInputWidth}
          error={!!error}
          selectedOption={options.find(
            (option) => option.value === (value as V[] | undefined)?.[0]
          )}
          onSelect={(next) => {
            const nextValue = [next];
            onChange(nextValue);
            onSelect?.(nextValue);
          }}
        />
        {isEmptyOrNull(error?.message) ? (
          !isEmptyOrNull(helperText) && (
            <HelperText type="info">{helperText}</HelperText>
          )
        ) : (
          <HelperText type="error">{error?.message}</HelperText>
        )}
      </View>
    )}
  />
);
